import { Controller } from "@hotwired/stimulus";
import IMask from "imask";
import { ImaskedElement } from "./imask_controller";

const generateNumericMask = (
  currency: string,
  decimals: number,
  thousandsSeparator: string,
  decimalsSeparator: string,
  stripInsignificantZerosValue: boolean
) => ({
  mask: `${currency} num`,
  blocks: {
    num: {
      mask: Number,
      thousandsSeparator,
      padFractionalZeros: !stripInsignificantZerosValue,
      signed: false,
      min: 0,
      normalizeZeros: true,
      radix: decimalsSeparator,
      mapToRadix: [decimalsSeparator],
      scale: decimals
    }
  }
});

const generateMask = (
  mode,
  currency: string,
  decimals: number,
  thousandsSeparator: string,
  decimalsSeparator: string,
  stripInsignificantZerosValue: boolean
) =>
  mode == "amount"
    ? generateNumericMask(
        currency,
        decimals,
        thousandsSeparator,
        decimalsSeparator,
        stripInsignificantZerosValue
      )
    : {
        mask: [
          {
            mask: ""
          },
          {
            mask: "num%",
            lazy: false,
            blocks: {
              num: {
                mask: Number,
                scale: decimals,
                min: 0,
                max: 100,
                thousandsSeparator,
                padFractionalZeros: !stripInsignificantZerosValue,
                normalizeZeros: true,
                radix: decimalsSeparator,
                mapToRadix: [decimalsSeparator]
              }
            }
          }
        ]
      };

export default class extends Controller {
  static targets = ["option", "input"];

  static values = {
    currency: String,
    decimals: Number,
    stripInsignificantZeros: { type: Boolean, default: false },
    thousandsSeparator: { type: String, default: "," },
    decimalsSeparator: { type: String, default: "." }
  };

  declare readonly currencyValue: string;
  declare readonly decimalsValue: number;
  declare readonly thousandsSeparatorValue: string;
  declare readonly decimalsSeparatorValue: string;
  declare readonly stripInsignificantZerosValue: boolean;
  declare readonly optionTargets: HTMLElement[];
  declare readonly inputTarget: ImaskedElement<HTMLInputElement>;

  mode = "amount";

  initialize() {
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyUp = this.handleInputKeyUp.bind(this);
    if (this.inputTarget.value.indexOf("%") >= 0) {
      this.mode = "percentage";
    }
  }

  connect() {
    this.updateImask();
    this.updateOptions();
    this.inputTarget.addEventListener("input", this.handleInputChange);
    this.inputTarget.addEventListener("keyup", this.handleInputKeyUp);
  }

  disconnect() {
    this.inputTarget.removeEventListener("input", this.handleInputChange);
    this.inputTarget.removeEventListener("keyup", this.handleInputKeyUp);
  }

  updateImask() {
    if (this.inputTarget.imask) {
      this.inputTarget.imask.destroy();
    }

    const options = generateMask(
      this.mode,
      this.currencyValue,
      this.decimalsValue,
      this.thousandsSeparatorValue,
      this.decimalsSeparatorValue,
      this.stripInsignificantZerosValue
    );

    this.inputTarget.imask = IMask(
      this.inputTarget,
      (this.inputTarget.imaskOptions = options)
    );
  }

  handleInputChange() {
    setTimeout(() => {
      this.updateOptions();
    });
  }

  handleInputKeyUp(e) {
    if (e.key === "%") {
      e.stopPropagation();
      e.preventDefault();
      this.mode = "percentage";
      this.updateImask();
      return false;
    }
  }

  updateOptions() {
    const options = generateNumericMask(
      this.currencyValue,
      this.decimalsValue,
      this.thousandsSeparatorValue,
      this.decimalsSeparatorValue,
      this.stripInsignificantZerosValue
    );
    const masked = IMask.createMask(options);
    const value = this.currentValue();
    this.optionTargets.forEach((option) => {
      if (option.dataset.type == "percentage") {
        const formattedValue = IMask.pipe(
          value,
          {
            mask: "num%",
            blocks: { num: { ...options.blocks.num, mapToRadix: ["."] } }
          },
          IMask.PIPE_TYPE.UNMASKED
        );
        option.innerHTML = `${parseFloat(value) > 100 ? 100 : formattedValue}%`;
      } else {
        masked.resolve(`${this.currencyValue} ${value}`);
        option.innerHTML = masked.value;
      }
    });
  }

  handleChangeType(e) {
    let value;
    this.mode = e.target.dataset.type;
    if (this.mode === "percentage") {
      value = this.currentValue();
      value = `${value > 100 ? 100 : value}%`;
    } else {
      value = `${this.currencyValue} ${this.currentValue()}`;
    }
    this.inputTarget.value = value;
    this.updateImask();
    this.inputTarget.dispatchEvent(new Event("change"));
    this.updateOptions();
    if (e.type !== "focus") {
      this.inputTarget.focus();
    }
  }

  currencyValueChanged() {
    this.updateImask();
    this.updateOptions();
  }

  currentValue() {
    if (this.inputTarget.imask) {
      return this.inputTarget.imask.unmaskedValue.replace("%", "");
    } else {
      return this.inputTarget.value.replace("%", "");
    }
  }
}
